import { Button, Divider, Grid, Header, Icon, Item, Segment } from "semantic-ui-react";
import { OrgRole } from "../models/OrgRole";
import { PermitType } from "../models/PermitType";
import { PermitTypeOrgRole } from "../models/PermitTypeOrgRole";
import { PermitTypeDocumentType } from "../models/PermitTypeDocumentType";
import { DocumentType } from "../models/DocumentType";
import PermitTypeRolesGrid from "./PermitTypeRolesGrid";
import PermitTypeDocumentTypesGrid from "./PermitTypeDocumentTypesGrid";
import { LicenceType } from "../models/globalTypes";
import { AsyncActionStatus } from "../models/AsyncActionStatus";
import { useEffect } from "react";
import { toastSuccess, toastError } from "../utils";

interface Props {
  permitType?: PermitType;
  permitTypeRoles?: PermitTypeOrgRole[];
  permitTypeDocumentTypes?: PermitTypeDocumentType[];
  rolesLoading?: boolean;
  documentTypesLoading?: boolean;
  availableOrgRoles: OrgRole[];
  availableDocumentTypes: DocumentType[];
  onClose: () => void;
  onAddRoles: (orgRoleIds: string[]) => void;
  onRoleSettings: (permitTypeOrgRole: PermitTypeOrgRole) => void;
  onRemoveRole: (permitTypeOrgRole: PermitTypeOrgRole) => void;
  removeRoleStatus: AsyncActionStatus;
  onAddDocumentTypes: (documentTypeIds: string[]) => void;
  onDocumentTypeSettings: (permitTypeDocumentType: PermitTypeDocumentType) => void;
  onRemoveDocumentType: (permitTypeDocumentType: PermitTypeDocumentType) => void;
  onOpenAddRoleModal: () => void;
  onOpenAddDocumentTypeModal: () => void;
}

const PermitTypeView = ({
  permitType,
  permitTypeRoles,
  permitTypeDocumentTypes,
  rolesLoading,
  documentTypesLoading,
  onClose,
  onOpenAddRoleModal,
  onOpenAddDocumentTypeModal,
  onRoleSettings,
  onRemoveRole,
  removeRoleStatus,
  onDocumentTypeSettings,
  onRemoveDocumentType,
}: Props) => {
  useEffect(() => {
    if (removeRoleStatus.successful) {
      toastSuccess("Role removed successfully!");
      resetMutationStatuses();
    } else if (removeRoleStatus.errors) {
      toastError(removeRoleStatus.errors.toString());
      resetMutationStatuses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeRoleStatus]);

  const resetMutationStatuses = () => {
    removeRoleStatus.reset();
  };

  const PermitTypeViewItem = (header: string, value?: string | null) => {
    return (
      <Item.Group>
        <Item>
          <Item.Content>
            <Item.Meta>{header}</Item.Meta>
            <Item.Description>{value ? value : ""}</Item.Description>
          </Item.Content>
        </Item>
      </Item.Group>
    );
  };

  return (
    <>
      {permitType && (
        <>
          <Segment attached="top" basic secondary compact>
            <Button
              circular
              icon="close"
              floated="right"
              style={{ padding: "5px", marginLeft: "8px", boxShadow: "0px 0px" }}
              onClick={onClose}
            />
            <Header as="h4" style={{ display: "inline" }}>
              Permit type details
            </Header>
          </Segment>

          <Segment attached color="green" style={{ borderTop: "3px !important" }}>
            <Grid>
              <Grid.Column width="16">
                <Grid columns="2">
                  <Grid.Row>
                    <Grid.Column>{PermitTypeViewItem("Name", permitType.name)}</Grid.Column>
                  </Grid.Row>
                  <Grid.Row>
                    <Grid.Column>{PermitTypeViewItem("Blueprint key", permitType.blueprintKey)}</Grid.Column>
                  </Grid.Row>
                </Grid>

                <Divider />

                <Button
                  circular
                  floated="right"
                  style={{ padding: "5px", marginLeft: "8px", boxShadow: "0px 0px" }}
                  onClick={onOpenAddRoleModal}
                >
                  <Icon size="small" name="plus" />
                  Add role
                </Button>
                <Header as="h4" style={{ display: "inline" }}>
                  Roles
                </Header>

                <Divider />

                <Segment basic loading={rolesLoading} style={{ padding: 0 }}>
                  <PermitTypeRolesGrid
                    permitTypeRoles={permitTypeRoles?.filter((r) => r.orgRole.licenceType !== LicenceType.VIEWER)}
                    onSettings={onRoleSettings}
                    onRemove={onRemoveRole}
                  />
                </Segment>

                <Divider />

                <Button
                  circular
                  floated="right"
                  style={{ padding: "5px", marginLeft: "8px", boxShadow: "0px 0px" }}
                  onClick={onOpenAddDocumentTypeModal}
                >
                  <Icon size="small" name="plus" />
                  Add document type
                </Button>
                <Header as="h4" style={{ display: "inline" }}>
                  Document types
                </Header>

                <Divider />

                <Segment basic loading={documentTypesLoading} style={{ padding: 0 }}>
                  <PermitTypeDocumentTypesGrid
                    permitTypeDocumentTypes={permitTypeDocumentTypes}
                    onSettings={onDocumentTypeSettings}
                    onRemove={onRemoveDocumentType}
                  />
                </Segment>
              </Grid.Column>
            </Grid>
          </Segment>
        </>
      )}
    </>
  );
};

export default PermitTypeView;
